import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Plus, Search, Settings2, Users } from 'lucide-react';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useReadOnly } from '../../contexts/ReadOnlyContext';
import { ConfirmationModal, PageLoader, Pagination } from '../../components/common';
import AddExpertModal from '../../components/expert/AddExpertModal';
import InviteLinkModal, { LinkModalData } from '../../components/expert/InviteLinkModal';
import SignInOptionsModal from '../../components/expert/SignInOptionsModal';
import ExpertAppLinkCard from '../../components/expert/ExpertAppLinkCard';
import {
  EmptyState,
  ErrorBanner,
  PAGE_SIZE,
  PageHeader,
  RowMenu,
  RowMenuItem,
  btnPrimary,
  btnSecondary,
  inputClass,
  useDebouncedValue,
} from '../../components/expert/ui';
import {
  ExpertItem,
  ExpertStatus,
  domainExpertApi,
  expertKeys,
  formatDate,
  getExpertErrorMessage,
  isRequestCancelled,
} from '../../lib/domain-expert-api';

type DisplayStatus = 'active' | 'invite_sent' | 'invite_expired' | 'disabled';

const STATUS_STYLES: Record<DisplayStatus, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-green-100 text-green-800' },
  invite_sent: { label: 'Invite sent', className: 'bg-indigo-100 text-indigo-800' },
  invite_expired: { label: 'Invite expired', className: 'bg-amber-100 text-amber-800' },
  disabled: { label: 'Disabled', className: 'bg-gray-200 text-gray-700' },
};

const getDisplayStatus = (expert: ExpertItem): DisplayStatus => {
  if (expert.status === 'disabled') return 'disabled';
  if (expert.status === 'active') return 'active';
  return expert.invite_state === 'pending' ? 'invite_sent' : 'invite_expired';
};

interface ConfirmState {
  title: string;
  message: string;
  confirmText: string;
  type?: 'danger' | 'warning' | 'info';
  action: () => Promise<void>;
}

export default function ExpertsPage() {
  const { hasExpert } = useWorkspace();
  const { isReadOnly } = useReadOnly();
  const queryClient = useQueryClient();

  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim());
  const [statusFilter, setStatusFilter] = useState<ExpertStatus | ''>('');
  const [page, setPage] = useState(1);

  const [addOpen, setAddOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [linkModal, setLinkModal] = useState<LinkModalData | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [busy, setBusy] = useState(false);

  const params = {
    search: search || undefined,
    status: statusFilter || undefined,
    skip: (page - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
  };

  const expertsQuery = useQuery({
    queryKey: expertKeys.experts(params),
    queryFn: () => domainExpertApi.listExperts(params),
    enabled: hasExpert,
    keepPreviousData: true,
  });

  const signInQuery = useQuery({
    queryKey: expertKeys.signIn(),
    queryFn: domainExpertApi.getSignInSettings,
    enabled: hasExpert,
  });

  const loginLinkQuery = useQuery({
    queryKey: expertKeys.loginLink(),
    queryFn: domainExpertApi.getLoginLink,
    enabled: hasExpert,
    staleTime: 5 * 60 * 1000,
  });

  const passwordsAllowed = signInQuery.data?.password_login_enabled !== false;
  const data = expertsQuery.data;
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const counts = data?.counts;
  const totalExperts = counts ? counts.invited + counts.active + counts.disabled : total;
  const isFiltered = !!search || !!statusFilter;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['expert', 'experts'] });

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      if (!isRequestCancelled(err)) toast.error(getExpertErrorMessage(err));
    } finally {
      setBusy(false);
      refresh();
    }
  };

  const nameOf = (expert: ExpertItem) => expert.full_name || expert.email;

  const sendInvite = (expert: ExpertItem) =>
    run(async () => {
      const result = await domainExpertApi.sendInvite(expert.id, true);
      if (result.email_sent) {
        toast.success(`Invite sent to ${expert.email}`);
      } else {
        setLinkModal({ kind: 'invite', url: result.invite_url, expiresAt: result.invite_expires_at, email: expert.email, emailSent: false });
      }
    });

  const menuItems = (expert: ExpertItem): RowMenuItem[] => {
    const name = nameOf(expert);
    const remove: RowMenuItem = {
      label: 'Remove',
      danger: true,
      onClick: () =>
        setConfirm({
          title: 'Remove user',
          message: `${name} will lose access to the expert app and be removed from this list. This can't be undone.`,
          confirmText: 'Remove',
          action: async () => {
            await domainExpertApi.deleteExpert(expert.id);
            toast.success('User removed');
          },
        }),
    };

    switch (getDisplayStatus(expert)) {
      case 'invite_sent':
        return [
          { label: 'Resend invite', onClick: () => sendInvite(expert) },
          {
            label: 'Copy invite link',
            onClick: () =>
              setConfirm({
                title: 'Copy invite link',
                message: 'This creates a new invite link. The link in the email we already sent will stop working.',
                confirmText: 'Create link',
                type: 'warning',
                action: async () => {
                  const result = await domainExpertApi.sendInvite(expert.id, false);
                  setLinkModal({ kind: 'invite', url: result.invite_url, expiresAt: result.invite_expires_at, email: expert.email, emailSent: null });
                },
              }),
          },
          {
            label: 'Cancel invite',
            onClick: () =>
              setConfirm({
                title: 'Cancel invite',
                message: `The invite link sent to ${expert.email} will stop working.`,
                confirmText: 'Cancel invite',
                type: 'warning',
                action: async () => {
                  await domainExpertApi.cancelInvite(expert.id);
                  toast.success('Invite cancelled');
                },
              }),
          },
          remove,
        ];
      case 'invite_expired':
        return [{ label: 'Send new invite', onClick: () => sendInvite(expert) }, remove];
      case 'active':
        return [
          ...(passwordsAllowed
            ? [
                {
                  label: 'Reset password',
                  onClick: () =>
                    run(async () => {
                      const result = await domainExpertApi.resetPassword(expert.id, true);
                      setLinkModal({ kind: 'reset', url: result.reset_url, expiresAt: result.expires_at, email: expert.email, emailSent: result.email_sent });
                    }),
                },
              ]
            : []),
          {
            label: 'Sign out everywhere',
            onClick: () =>
              setConfirm({
                title: 'Sign out everywhere',
                message: `${name} will be signed out of the expert app on every device.`,
                confirmText: 'Sign out',
                type: 'warning',
                action: async () => {
                  await domainExpertApi.revokeSessions(expert.id);
                  toast.success(`${name} has been signed out everywhere`);
                },
              }),
          },
          {
            label: 'Disable',
            onClick: () =>
              setConfirm({
                title: 'Disable user',
                message: `${name} will lose access to the expert app. You can restore them later.`,
                confirmText: 'Disable',
                action: async () => {
                  await domainExpertApi.updateExpert(expert.id, { status: 'disabled' });
                  toast.success('User disabled');
                },
              }),
          },
          remove,
        ];
      case 'disabled':
        return [
          {
            label: 'Restore',
            onClick: () =>
              run(async () => {
                await domainExpertApi.updateExpert(expert.id, { status: 'active' });
                toast.success('User restored');
              }),
          },
          remove,
        ];
    }
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    const { action } = confirm;
    await run(action);
    setConfirm(null);
  };

  if (expertsQuery.isInitialLoading) return <PageLoader fullScreen={false} />;

  return (
    <div>
      <PageHeader
        title="Users"
        description="People who can sign in to your expert app."
        actions={
          <>
            <button type="button" onClick={() => setSignInOpen(true)} className={btnSecondary}>
              <Settings2 className="mr-2 h-4 w-4" />
              Sign-in options
            </button>
            <button type="button" onClick={() => setAddOpen(true)} disabled={isReadOnly} className={btnPrimary}>
              <Plus className="mr-2 h-4 w-4" />
              Add user
            </button>
          </>
        }
      />

      <div className="mt-6">
        <ExpertAppLinkCard loginUrl={loginLinkQuery.data?.login_url} isLoading={loginLinkQuery.isInitialLoading} />
      </div>

      {expertsQuery.isError && <ErrorBanner message={getExpertErrorMessage(expertsQuery.error, "We couldn't load your users.")} />}

      {(totalExperts > 0 || isFiltered) && (
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative sm:max-w-xs sm:flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              placeholder="Search by name or email"
              aria-label="Search users"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPage(1);
              }}
              className={`${inputClass} pl-9`}
            />
          </div>
          {(totalExperts > 10 || statusFilter) && (
            <select
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as ExpertStatus | '');
                setPage(1);
              }}
              className={`${inputClass} sm:w-48`}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="invited">Invited</option>
              <option value="disabled">Disabled</option>
            </select>
          )}
        </div>
      )}

      <div className="mt-4 overflow-x-auto bg-white shadow ring-1 ring-black ring-opacity-5">
        {items.length === 0 ? (
          isFiltered ? (
            <EmptyState title="No users match your search." />
          ) : (
            <EmptyState icon={<Users className="h-12 w-12" />} title="No users yet.">
              <p>Add your first user to give them access to your expert app.</p>
              {!isReadOnly && (
                <button type="button" onClick={() => setAddOpen(true)} className={`${btnPrimary} mt-4`}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add user
                </button>
              )}
            </EmptyState>
          )
        ) : (
          <table className="w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                {['Name', 'Email', 'Status', 'Last active'].map((heading) => (
                  <th key={heading} scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {heading}
                  </th>
                ))}
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {items.map((expert) => {
                const status = STATUS_STYLES[getDisplayStatus(expert)];
                const lastActive = expert.last_seen_at ?? expert.last_login_at;
                return (
                  <tr key={expert.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">{expert.full_name || '—'}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">{expert.email}</td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${status.className}`}>{status.label}</span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{lastActive ? formatDate(lastActive) : 'Never'}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      {!isReadOnly && <RowMenu label={`Actions for ${nameOf(expert)}`} items={busy ? [] : menuItems(expert)} />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {total > PAGE_SIZE && (
        <Pagination
          className="mt-4"
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          rangeStart={(page - 1) * PAGE_SIZE + 1}
          rangeEnd={Math.min(page * PAGE_SIZE, total)}
          totalItems={total}
          itemLabel="users"
        />
      )}

      <AddExpertModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(result) => {
          setAddOpen(false);
          if (result.invite_url) {
            setLinkModal({
              kind: 'invite',
              url: result.invite_url,
              expiresAt: result.invite_expires_at,
              email: result.expert.email,
              emailSent: result.email_sent,
            });
          } else {
            toast.success(`${nameOf(result.expert)} added`);
          }
        }}
      />

      <InviteLinkModal data={linkModal} onClose={() => setLinkModal(null)} />

      <SignInOptionsModal isOpen={signInOpen} onClose={() => setSignInOpen(false)} enabled={hasExpert} readOnly={isReadOnly} />

      <ConfirmationModal
        isOpen={!!confirm}
        onClose={() => !busy && setConfirm(null)}
        onConfirm={handleConfirm}
        title={confirm?.title ?? ''}
        message={confirm?.message ?? ''}
        confirmText={confirm?.confirmText}
        type={confirm?.type ?? 'danger'}
        isLoading={busy}
      />
    </div>
  );
}
